/**
 * CoverageTab — the Couverture tab.
 *
 * Step 8 wiring:
 * - Measured cell click → calls onFilter(filter) with fromCell(triple, members).
 * - Hatched cell click → calls onCell(triple) to open the composer seeded.
 * - Axis selector: segmented tabs over the offered axes (one at a time).
 * - Unclassified rows: excluded from matrix, counted in foot line.
 * - While PROMPT_ZONE_EDITING is false, only Mondial hatched cells open the
 *   composer (non-Mondial hatched cells show hatch + title, not clickable).
 *
 * @see B-popup-frontend.md step 8, UI-SPEC §2.18
 */
import { Box } from '@mui/material';
import type { BrandQueryAxis, BrandStrategy, Location } from '@traaker/shared';
import { zoneDisplayLabel } from '@traaker/shared';
import type { Family, LocKey, CellTriple } from './promptStudioModel';
import {
  AXIS_COLOR,
  MONDIAL,
  PROMPT_ZONE_EDITING,
} from './promptStudioModel';
import {
  vocabFor,
  coverageMatrix,
  covShade,
  COV_HATCH_SX,
  unclassifiedCount,
  cellTriple,
  covMembers,
} from './coverageModel';
import type { CovConversation } from './coverageModel';
import type { FilterState } from './filterModel';
import { fromCell } from './filterModel';

export interface CoverageTabProps {
  families: Family[];
  convs: CovConversation[];
  ax: BrandQueryAxis;
  axes: readonly BrandQueryAxis[];
  onAxis: (ax: BrandQueryAxis) => void;
  zr: boolean;
  locs: LocKey[];
  locations: Location[];
  known: Map<LocKey, import('@traaker/shared').TargetZone>;
  strategy?: BrandStrategy;
  /** Measured cell click → sets filter + switches to prompts tab */
  onFilter: (filter: FilterState, ax: BrandQueryAxis) => void;
  /** Hatched Mondial cell click → open composer seeded with this cell */
  onCell: (cell: CellTriple) => void;
  /** Lot I3: a cell's 🤖 count, or a cell with conversations only → the Conversations tab filtered on it. */
  onConvCell?: (cell: CellTriple) => void;
  onPending?: () => void;
  onUnclassified?: () => void;
  onAddLoc?: () => void;
  pendingCount?: number;
}

const AXIS_FR: Record<BrandQueryAxis, string> = {
  // i18n: promptStudio.axis.*
  narrative: 'Image',
  authority: 'Autorité',
  referral: 'Recommandation',
  comparison: 'Comparaison',
};

export function CoverageTab({
  families,
  convs,
  ax,
  axes,
  onAxis,
  zr,
  locs,
  locations,
  known,
  strategy,
  onFilter,
  onCell,
  onConvCell,
  onPending,
  onUnclassified,
  onAddLoc,
  pendingCount = 0,
}: CoverageTabProps) {
  const vocab = vocabFor(strategy, ax);
  const uncCount = unclassifiedCount(families, ax, vocab);

  const { rowKeys, colKeys, cells } = coverageMatrix(
    families, convs, ax, vocab, locs, zr, locations, known,
  );

  // Labels
  const locLabel = (key: LocKey | string | null) => {
    if (key === null) return '(aucun)'; // i18n: promptStudio.vocab.none
    if (key === MONDIAL) return 'Mondial'; // i18n: promptStudio.place.mondial
    if (typeof key === 'string' && key.startsWith('legacy:')) return key.slice(7);
    const zone = known.get(key as LocKey);
    if (zone) {
      // An address reads « street (city) », never its city alone (owner, 2026-10-30).
      return zoneDisplayLabel(zone) || key;
    }
    return key as string;
  };
  // zr → rows are places, columns vocabulary; else the reverse (coverageMatrix).
  const keyLabel = (key: LocKey | string | null, isLoc: boolean) =>
    isLoc ? locLabel(key) : key === null ? '(aucun)' : (key as string); // i18n: promptStudio.vocab.none

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Axis selector */}
      <Box sx={{ display: 'flex', gap: 0.5, px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, alignItems: 'center' }}>
        {axes.map((a) => (
          <Box
            key={a}
            component="button"
            onClick={() => onAxis(a)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: a === ax ? AXIS_COLOR[a] : 'divider',
              bgcolor: a === ax ? `${AXIS_COLOR[a]}18` : 'transparent',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: a === ax ? 600 : 400,
              color: a === ax ? AXIS_COLOR[a] : 'text.secondary',
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: AXIS_COLOR[a], opacity: a === ax ? 1 : 0.35, flexShrink: 0 }} />
            {AXIS_FR[a]}
          </Box>
        ))}
        <Box sx={{ flex: 1 }} />
        {/* Legend + vocab count */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontSize: 11, color: 'text.disabled' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: '#7c3aed40' }} />
            {/* i18n: promptStudio.cov.promptsLegend */}
            💬 prompts
          </Box>
          {convs.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* i18n: promptStudio.cov.convsLegend */}
              🤖 conversations
            </Box>
          )}
          {vocab.length > 0 && (
            <Box sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>
              {vocab.length} {ax === 'authority' ? 'thèmes' : ax === 'referral' ? 'cas d\'usage' : 'critères'}
            </Box>
          )}
        </Box>
      </Box>

      {/* Matrix */}
      <Box sx={{ flex: 1, overflowX: 'auto', overflowY: 'auto' }}>
        <Box component="table" sx={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: 300 }}>
          {/* Header row */}
          <Box component="thead">
            <Box component="tr">
              <Box
                component="th"
                sx={{
                  position: 'sticky',
                  left: 0,
                  top: 0,
                  zIndex: 3,
                  bgcolor: '#fafbfd',
                  px: 1.5,
                  py: 1,
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '.04em',
                  color: 'text.secondary',
                  borderBottom: '2px solid',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  minWidth: 140,
                }}
              >
                {zr
                  ? 'Lieu' // i18n: promptStudio.cov.locHeader
                  : ax === 'authority' ? 'Thème' : ax === 'referral' ? 'Cas d\'usage' : 'Critère'}
              </Box>
              {colKeys.map((colKey, ci) => (
                <Box
                  key={`lh-${ci}`}
                  component="th"
                  data-cov-lane={!zr ? ci : undefined}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    bgcolor: '#fafbfd',
                    px: 1,
                    py: 1,
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontStyle: zr && colKey === null ? 'italic' : 'normal',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    minWidth: 72,
                  }}
                >
                  {keyLabel(colKey, !zr)}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Body rows */}
          <Box component="tbody">
            {rowKeys.map((rowKey, ri) => {
              const isNone = !zr && rowKey === null;

              return (
                <Box
                  key={`row-${ri}`}
                  component="tr"
                  sx={{
                    '&:hover td': { bgcolor: 'rgba(0,0,0,.01)' },
                  }}
                >
                  {/* Vocab label */}
                  <Box
                    component="td"
                    data-cov-lane={zr ? ri : undefined}
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      bgcolor: 'background.paper',
                      px: 1.5,
                      py: 0.75,
                      fontSize: 12,
                      color: isNone ? 'text.disabled' : 'text.primary',
                      fontStyle: isNone ? 'italic' : 'normal',
                      borderRight: '1px solid',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      whiteSpace: 'nowrap',
                      maxWidth: 200,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {keyLabel(rowKey, zr)}
                  </Box>

                  {/* Cells */}
                  {colKeys.map((colKey, ci) => {
                    const cell = cells[ri]?.[ci] ?? null;
                    const locKey = zr ? rowKey : colKey;
                    const isMondial = locKey === MONDIAL;
                    const isEmpty = cell === null || (cell.p === 0 && cell.c === 0);
                    // Hatched: no data. While !PROMPT_ZONE_EDITING only Mondial is openable; after,
                    // a lane with a known zone too (an unresolved legacy lane has none to seed).
                    const isHatched = isEmpty && cell === null;
                    const canOpen = isHatched && (isMondial || (PROMPT_ZONE_EDITING && known.has(locKey as LocKey)));
                    const isMeasured = !isEmpty && cell !== null && (cell.p > 0 || cell.c > 0);

                    const { bg, fg } = isMeasured ? covShade(cell.p + cell.c) : { bg: 'transparent', fg: 'transparent' };

                    const triple = cellTriple(zr, rowKey, colKey, ax);
                    const members = covMembers(families, locKey as LocKey, locations, known);

                    const handleClick = () => {
                      if (isMeasured && cell && cell.p === 0 && onConvCell) {
                        // Conversations only → the Conversations tab on this cell (UI-SPEC §6.6)
                        onConvCell(triple);
                      } else if (isMeasured) {
                        // Measured cell → filter to this cell's rows
                        onFilter(fromCell(triple, members), ax);
                      } else if (canOpen) {
                        // Hatched Mondial cell → open composer seeded
                        onCell(triple);
                      }
                    };

                    return (
                      <Box
                        key={`cell-${ci}`}
                        component="td"
                        onClick={handleClick}
                        title={
                          isHatched && !canOpen
                            ? 'non mesuré' // i18n
                            : undefined
                        }
                        sx={{
                          px: 1,
                          py: 0.75,
                          textAlign: 'center',
                          fontSize: 12,
                          fontWeight: 600,
                          bgcolor: isHatched ? 'transparent' : bg,
                          color: fg,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          cursor: isMeasured ? 'pointer' : canOpen ? 'pointer' : 'default',
                          minWidth: 72,
                          ...(isHatched ? COV_HATCH_SX : {}),
                          position: 'relative',
                          '&:hover .cov-plus': { opacity: 1 },
                          '&:hover': isMeasured
                            ? { outline: '2px solid #7c3aed', outlineOffset: '1px' }
                            : {},
                        }}
                      >
                        {isMeasured && cell && (
                          <>
                            {cell.p > 0 && <span>{cell.p}</span>}
                            {cell.c > 0 && (
                              <Box
                                component="span"
                                title="Voir ces conversations" // i18n: promptStudio.cov.seeConversations
                                onClick={(e: React.MouseEvent) => {
                                  if (!onConvCell) return;
                                  e.stopPropagation();
                                  onConvCell(triple);
                                }}
                                sx={{ ml: cell.p > 0 ? 1 : 0, borderRadius: 1, px: 0.25, '&:hover': onConvCell ? { boxShadow: 'inset 0 0 0 1.5px currentColor' } : {} }}
                              >
                                🤖{cell.c}
                              </Box>
                            )}
                          </>
                        )}
                        {canOpen && (
                          <Box
                            className="cov-plus"
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 18,
                              color: '#7c3aed',
                              opacity: 0,
                              transition: 'opacity .15s',
                            }}
                          >
                            +
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider', fontSize: 12, color: 'text.secondary', flexShrink: 0 }}>
        {onAddLoc && PROMPT_ZONE_EDITING && (
          <Box
            component="button"
            onClick={onAddLoc}
            sx={{ fontSize: 12, color: '#7c3aed', border: '1px dashed', borderColor: 'rgba(124,58,237,.45)', borderRadius: 1, px: 1, py: 0.25, bgcolor: 'transparent', cursor: 'pointer' }}
          >
            {/* i18n: promptStudio.cov.addLoc */}
            + Ajouter une localisation
          </Box>
        )}
        {pendingCount > 0 && (
          <Box
            component="button"
            onClick={onPending}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: 10,
              border: '1px solid #f59e0b',
              bgcolor: '#fffbeb',
              fontSize: 11.5,
              color: '#b45309',
              cursor: 'pointer',
            }}
          >
            {/* i18n: promptStudio.cov.pending */}
            {pendingCount} en attente
          </Box>
        )}
        {uncCount > 0 && (
          <Box
            component="button"
            onClick={onUnclassified}
            title="Classés quand leur texte est modifié et validé" // i18n
            sx={{ fontSize: 11.5, color: 'text.secondary', border: 'none', bgcolor: 'transparent', cursor: 'pointer', textDecoration: 'underline' }}
          >
            {/* i18n: promptStudio.cov.unclassified */}
            {uncCount} prompts non classés
          </Box>
        )}
      </Box>
    </Box>
  );
}
